import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import * as echarts from "echarts";
import { colors } from "../../../utils/colors";

export interface GenericLineChartPoint {
  primary: number;
  secondary?: number;
}

export type GenericLineChartDataValue = number | GenericLineChartPoint;

export interface GenericLineChartSeries {
  name: string;
  data: GenericLineChartDataValue[];
  color?: string;
}

const isChartPoint = (
  value: GenericLineChartDataValue,
): value is GenericLineChartPoint =>
  typeof value === "object" && value !== null && "primary" in value;

const getPrimaryValue = (value: GenericLineChartDataValue): number =>
  isChartPoint(value) ? value.primary : value;

const toEchartsDataPoint = (value: GenericLineChartDataValue) => {
  if (isChartPoint(value)) {
    return {
      value: value.primary,
      secondary: value.secondary,
    };
  }
  return value;
};

export interface GenericLineChartProps {
  title?: string;
  titleAlign?: "left" | "center" | "right";
  categories: string[];
  series: GenericLineChartSeries[];
  loading?: boolean;
  height?: number;
  minHeight?: number;
  emptyMessage?: string;
  showLegend?: boolean;
  showEndLabels?: boolean;
  legendTop?: number;
  legendRight?: number;
  xAxisLabelColor?: string;
  xAxisLabelFontSize?: string;
  xAxisLabelFontWeight?: number;
  yAxisLabelColor?: string;
  yAxisLabelFontSize?: string;
  xAxisRotateThreshold?: number;
  tooltipValueFormatter?: (value: number) => string;
  secondaryTooltipValueFormatter?: (value: number) => string;
  axisTooltipFormatter?: (ctx: {
    dataIndex: number;
    categoryLabel: string;
    seriesName: string;
    primaryValue: number;
    secondaryValue?: number;
    seriesMarkerHtml: string;
  }) => string;
  yAxisValueFormatter?: (value: number) => string;
  resizeTrigger?: string | number | boolean | null;
  defaultSeriesColors?: string[];
}

const DEFAULT_SERIES_COLORS = [colors.chart_blue, colors.chart_gray];

type TooltipParamRow = {
  seriesName?: string;
  value?: number;
  data?: { secondary?: number };
  marker?: string;
  dataIndex?: number;
  seriesIndex?: number;
};

type AxisTooltipParam = {
  dataIndex?: number;
  axisValue?: string;
};

const TOOLTIP_OUTSIDE_CHART = {
  appendToBody: true,
  confine: false,
  extraCssText: "z-index: 10000;",
  textStyle: {
    color: colors.chart_text,
  },
};

const resolveSeriesIndex = (
  row: TooltipParamRow,
  chartSeries: GenericLineChartSeries[],
): number =>
  row.seriesIndex ?? chartSeries.findIndex((s) => s.name === row.seriesName);

const resolveLineColor = (
  chartSeries: GenericLineChartSeries[],
  seriesIdx: number,
  defaultSeriesColors: string[],
): string =>
  chartSeries[seriesIdx]?.color ??
  defaultSeriesColors[
    seriesIdx >= 0 ? seriesIdx % defaultSeriesColors.length : 0
  ];

const buildSeriesMarkerHtml = (
  marker: string | undefined,
  lineColor: string,
): string => {
  const markerFromSeries = marker?.trim();
  if (markerFromSeries && markerFromSeries.length > 0) {
    return markerFromSeries;
  }
  return `<span style="display:inline-block;margin-right:5px;width:10px;height:10px;border-radius:50%;background-color:${lineColor};vertical-align:middle;"></span>`;
};

const buildDefaultAxisTooltip = (
  rawParams: unknown,
  categories: string[],
  chartSeries: GenericLineChartSeries[],
  defaultSeriesColors: string[],
  formatPrimaryTooltip: (value: number) => string,
  formatSecondaryTooltip: (value: number) => string,
): string => {
  const params = Array.isArray(rawParams) ? rawParams : [rawParams];
  const first = params[0] as AxisTooltipParam | undefined;
  const idx = first?.dataIndex ?? 0;
  const categoryLabel = String(first?.axisValue ?? categories[idx] ?? "");
  const rowsHtml = params
    .map((item) => {
      const row = item as TooltipParamRow;
      const seriesIdx = resolveSeriesIndex(row, chartSeries);
      const lineColor = resolveLineColor(
        chartSeries,
        seriesIdx,
        defaultSeriesColors,
      );
      const seriesMarkerHtml = buildSeriesMarkerHtml(row.marker, lineColor);
      const primaryValue = Number(row.value ?? 0);
      const secondaryValue = row.data?.secondary;
      const primaryText = formatPrimaryTooltip(primaryValue);
      const secondaryText =
        secondaryValue === undefined
          ? ""
          : ` (<strong>${formatSecondaryTooltip(secondaryValue)}</strong>)`;
      return `<span style="display:inline-block;margin-top:6px;">${seriesMarkerHtml}${row.seriesName ?? ""} <span style="display:inline-block;padding-left:14px;"><strong>${primaryText}</strong>${secondaryText}</span></span>`;
    })
    .join("<br/>");
  return `<div style="color:${colors.chart_text};">${categoryLabel}<br/>${rowsHtml}</div>`;
};

const buildCustomAxisTooltip = (
  rawParams: unknown,
  categories: string[],
  chartSeries: GenericLineChartSeries[],
  defaultSeriesColors: string[],
  axisTooltipFormatter: NonNullable<
    GenericLineChartProps["axisTooltipFormatter"]
  >,
): string => {
  const params = Array.isArray(rawParams) ? rawParams : [rawParams];
  const first = params[0] as AxisTooltipParam | undefined;
  const idx = first?.dataIndex ?? 0;
  const categoryLabel = String(first?.axisValue ?? categories[idx] ?? "");
  return params
    .map((item) => {
      const row = item as TooltipParamRow;
      const seriesIdx = resolveSeriesIndex(row, chartSeries);
      const lineColor = resolveLineColor(
        chartSeries,
        seriesIdx,
        defaultSeriesColors,
      );
      return axisTooltipFormatter({
        dataIndex: row.dataIndex ?? idx,
        categoryLabel,
        seriesName: row.seriesName ?? "",
        primaryValue: Number(row.value ?? 0),
        secondaryValue: row.data?.secondary,
        seriesMarkerHtml: buildSeriesMarkerHtml(row.marker, lineColor),
      });
    })
    .join("");
};

const formatEndLabel =
  (lastCategoryIndex: number) =>
  (params: { dataIndex?: number; seriesName?: string }) =>
    params.dataIndex === lastCategoryIndex ? (params.seriesName ?? "") : "";

const defaultYAxisValueFormatter = (val: number) =>
  Number(val).toLocaleString("en-US");

export const GenericLineChart: React.FC<GenericLineChartProps> = ({
  title,
  titleAlign = "left",
  categories,
  series,
  loading = false,
  height = 400,
  minHeight = 400,
  emptyMessage = "No data available for the selected filters.",
  showLegend = false,
  showEndLabels = true,
  legendTop = 8,
  legendRight = 50,
  xAxisLabelColor = "#000000",
  xAxisLabelFontSize = "0.625rem",
  xAxisLabelFontWeight = 500,
  yAxisLabelColor = "#666666",
  yAxisLabelFontSize = "0.6875rem",
  xAxisRotateThreshold = 8,
  tooltipValueFormatter,
  secondaryTooltipValueFormatter,
  axisTooltipFormatter,
  yAxisValueFormatter,
  resizeTrigger,
  defaultSeriesColors = DEFAULT_SERIES_COLORS,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const resizeRafRef = useRef<number | null>(null);
  const hasSecondaryValues = series.some((s) =>
    s.data.some((v) => isChartPoint(v) && v.secondary !== undefined),
  );

  const hasData =
    categories.length > 0 &&
    series.length > 0 &&
    series.some((s) => s.data.length > 0);

  const scheduleResize = useCallback(() => {
    if (resizeRafRef.current !== null) {
      cancelAnimationFrame(resizeRafRef.current);
    }
    resizeRafRef.current = requestAnimationFrame(() => {
      resizeRafRef.current = null;
      chartInstance.current?.resize();
    });
  }, []);

  const defaultTooltipValueFormatter = useCallback(
    (v: number) =>
      Number(v).toLocaleString("en-US", {
        maximumFractionDigits: 2,
      }),
    [],
  );

  const formatPrimaryTooltip =
    tooltipValueFormatter ?? defaultTooltipValueFormatter;
  const formatSecondaryTooltip =
    secondaryTooltipValueFormatter ?? defaultTooltipValueFormatter;

  const option = useMemo(() => {
    const lastCategoryIndex = categories.length - 1;

    let tooltip: {
      trigger: "axis";
      axisPointer: { type: "line" };
      renderMode?: "html";
      valueFormatter?: (value: number) => string;
      formatter?: (rawParams: unknown) => string;
      appendToBody: boolean;
      confine: boolean;
      extraCssText: string;
      textStyle: { color: string };
    };

    if (axisTooltipFormatter !== undefined) {
      tooltip = {
        trigger: "axis",
        axisPointer: { type: "line" },
        renderMode: "html",
        ...TOOLTIP_OUTSIDE_CHART,
        formatter: (rawParams: unknown) =>
          buildCustomAxisTooltip(
            rawParams,
            categories,
            series,
            defaultSeriesColors,
            axisTooltipFormatter,
          ),
      };
    } else if (hasSecondaryValues) {
      tooltip = {
        trigger: "axis",
        axisPointer: { type: "line" },
        renderMode: "html",
        ...TOOLTIP_OUTSIDE_CHART,
        formatter: (rawParams: unknown) =>
          buildDefaultAxisTooltip(
            rawParams,
            categories,
            series,
            defaultSeriesColors,
            formatPrimaryTooltip,
            formatSecondaryTooltip,
          ),
      };
    } else {
      tooltip = {
        trigger: "axis",
        axisPointer: { type: "line" },
        valueFormatter: formatPrimaryTooltip,
        ...TOOLTIP_OUTSIDE_CHART,
      };
    }

    return {
      tooltip,
      legend: {
        show: showLegend,
        data: series.map((s) => s.name),
        top: legendTop,
        right: legendRight,
        orient: "horizontal",
        icon: "roundRect",
        itemWidth: 14,
        itemHeight: 14,
        itemGap: 16,
      },
      grid: {
        left: "5%",
        right: showEndLabels ? "14%" : "5%",
        top: showLegend && !showEndLabels ? "14%" : "8%",
        bottom: "18%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: categories,
        boundaryGap: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          fontSize: xAxisLabelFontSize,
          color: xAxisLabelColor,
          rotate: categories.length > xAxisRotateThreshold ? 35 : 0,
          fontWeight: xAxisLabelFontWeight,
        },
      },
      yAxis: {
        type: "value",
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          show: true,
        },
        axisLabel: {
          fontSize: yAxisLabelFontSize,
          color: yAxisLabelColor,
          formatter: yAxisValueFormatter ?? defaultYAxisValueFormatter,
        },
      },
      series: series.map((item, index) => {
        const lineColor =
          item.color ?? defaultSeriesColors[index % defaultSeriesColors.length];
        return {
          name: item.name,
          type: "line",
          data: item.data.map(toEchartsDataPoint),
          color: lineColor,
          symbol: "circle",
          symbolSize: 8,
          lineStyle: {
            width: 2,
            color: lineColor,
          },
          itemStyle: {
            color: lineColor,
            borderColor: lineColor,
            borderWidth: 2,
          },
          label: {
            show: showEndLabels,
            position: "right",
            color: colors.black,
            fontSize: 10,
            fontWeight: 500,
            formatter: formatEndLabel(lastCategoryIndex),
          },
          emphasis: {
            focus: "series",
          },
        };
      }),
    };
  }, [
    axisTooltipFormatter,
    categories,
    defaultSeriesColors,
    formatPrimaryTooltip,
    formatSecondaryTooltip,
    hasSecondaryValues,
    legendRight,
    legendTop,
    series,
    showEndLabels,
    showLegend,
    xAxisLabelColor,
    xAxisLabelFontSize,
    xAxisLabelFontWeight,
    xAxisRotateThreshold,
    yAxisLabelColor,
    yAxisLabelFontSize,
    yAxisValueFormatter,
  ]);

  useEffect(() => {
    if (!hasData) {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      chartInstance.current?.dispose();
      chartInstance.current = null;
      return undefined;
    }

    const dom = chartRef.current;
    if (!dom) {
      return undefined;
    }

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(dom);
      const ro = new ResizeObserver(() => {
        scheduleResize();
      });
      ro.observe(dom);
      resizeObserverRef.current = ro;
      globalThis.addEventListener("resize", scheduleResize);
    }

    return () => {
      if (resizeRafRef.current !== null) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      globalThis.removeEventListener("resize", scheduleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [hasData, scheduleResize]);

  useEffect(() => {
    if (!hasData || !chartInstance.current) {
      return;
    }
    chartInstance.current.setOption(option, {
      notMerge: true,
      lazyUpdate: true,
    });
    scheduleResize();
  }, [hasData, option, scheduleResize]);

  useEffect(() => {
    if (resizeTrigger === undefined) return;
    const timer = globalThis.setTimeout(() => scheduleResize(), 350);
    return () => globalThis.clearTimeout(timer);
  }, [resizeTrigger, scheduleResize]);

  if (!hasData && !loading) {
    return (
      <Box>
        {title ? (
          <Typography
            sx={{
              px: 2,
              mb: "0.25rem",
              fontWeight: 500,
              fontSize: "16px",
              color: colors.light_black,
              textAlign: titleAlign,
            }}
          >
            {title}
          </Typography>
        ) : null}
        <Box
          sx={{
            minWidth: 0,
            px: 2,
            py: 6,
            minHeight: 280,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {emptyMessage}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      {title ? (
        <Typography
          sx={{
            px: 2,
            mb: "0.25rem",
            fontWeight: 500,
            fontSize: "16px",
            color: colors.light_black,
            textAlign: titleAlign,
          }}
        >
          {title}
        </Typography>
      ) : null}
      <Box
        sx={{
          minHeight,
          px: 2,
          opacity: loading && !hasData ? 0.5 : 1,
          minWidth: 0,
        }}
      >
        {hasData ? (
          <Box ref={chartRef} sx={{ width: "100%", minWidth: 0, height }} />
        ) : (
          <Box sx={{ width: "100%", minHeight, minWidth: 0 }} />
        )}
      </Box>
    </Box>
  );
};
